// Due-date helpers. Dates are stored as epoch ms of local midnight on the
// chosen calendar day — they represent "the day this is due," not a specific
// instant, so we always convert through local calendar boundaries.

import { Task } from './types';

export function dateInputToMillis(value: string): number | null {
  // value is 'YYYY-MM-DD' (the format <input type="date"> emits).
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || '');
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  if (!y || !mo || !d) return null;
  return new Date(y, mo - 1, d).getTime();
}

export function millisToDateInput(ms: number): string {
  const d = new Date(ms);
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${mo}-${day}`;
}

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

const MONTHS_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

export function formatMonthDay(ms: number, opts: { withYear?: boolean } = {}): string {
  const d = new Date(ms);
  const m = MONTHS_SHORT[d.getMonth()];
  const day = d.getDate();
  return opts.withYear ? `${m} ${day}, ${d.getFullYear()}` : `${m} ${day}`;
}

export type DueTone = 'overdue' | 'today' | 'soon' | 'normal' | 'done';

export interface DueDisplay {
  label: string;
  tone: DueTone;
}

// Compact, status-aware label for a task's due date. `now` is injected so
// callers can pass a stable reference (test fixtures, periodic re-render).
// `taskDone` muffles the styling when the work's already finished — no point
// flagging a completed task as "overdue."
export function formatDueDate(ms: number, now: number, taskDone: boolean): DueDisplay {
  const today = startOfDay(new Date(now));
  const dueDay = startOfDay(new Date(ms));
  const diffDays = Math.round((dueDay.getTime() - today.getTime()) / 86400000);

  if (taskDone) {
    return { label: formatMonthDay(ms), tone: 'done' };
  }
  if (diffDays < 0) {
    const n = Math.abs(diffDays);
    return { label: n === 1 ? 'Overdue 1d' : `Overdue ${n}d`, tone: 'overdue' };
  }
  if (diffDays === 0) return { label: 'Due today', tone: 'today' };
  if (diffDays === 1) return { label: 'Due tomorrow', tone: 'soon' };
  if (diffDays <= 7) return { label: `Due in ${diffDays}d`, tone: 'soon' };
  // Beyond a week, switch to the calendar date so the user has an anchor.
  const sameYear = new Date(ms).getFullYear() === new Date(now).getFullYear();
  return { label: `Due ${formatMonthDay(ms, { withYear: !sameYear })}`, tone: 'normal' };
}

// Earliest + latest dueDate across a workstream's tasks. Tasks without a
// due date are ignored; if none are dated, returns null so the caller can
// render "no range" instead of a weird single-day chip.
export function workstreamDateRange(tasks: Task[]): { min: number; max: number } | null {
  let min = Infinity;
  let max = -Infinity;
  for (const t of tasks) {
    if (typeof t.dueDate !== 'number') continue;
    if (t.dueDate < min) min = t.dueDate;
    if (t.dueDate > max) max = t.dueDate;
  }
  if (min === Infinity) return null;
  return { min, max };
}

export function formatDateRange(min: number, max: number, now: number = Date.now()): string {
  const a = new Date(min);
  const b = new Date(max);
  const nowYear = new Date(now).getFullYear();
  const sameYear = a.getFullYear() === b.getFullYear();
  const allCurrentYear = sameYear && a.getFullYear() === nowYear;
  if (min === max) {
    return formatMonthDay(min, { withYear: !allCurrentYear });
  }
  // Same month + year — "May 18 – 22" reads tighter than "May 18 – May 22"
  if (sameYear && a.getMonth() === b.getMonth()) {
    const head = formatMonthDay(min);
    const yearSuffix = allCurrentYear ? '' : `, ${a.getFullYear()}`;
    return `${head} – ${b.getDate()}${yearSuffix}`;
  }
  // Different months — show both anchors. Add year only if span crosses years
  // or neither end is in the current year.
  const showYear = !allCurrentYear;
  return `${formatMonthDay(min, { withYear: showYear })} – ${formatMonthDay(max, { withYear: showYear })}`;
}
